import type { ExtendedOption } from "@hightouch/formkit";

import type { ColumnOption } from "src/formkit/components/formkit-context";
import { type Mapping, MappingType } from "src/formkit/components/types";
import type { Mapping as SyncConfigMapping } from "src/pages/syncs/sync/matchbooster";
import { type ColumnReference, isMergedColumn } from "src/types/visual";
import type { Option } from "src/ui/select";

// The advanced association mapper is only available for Salesforce because it does not use the associations framework.
// Do not include other destinations that use the associations framework until we adjust the framework to handle object type references.
export const ADVANCED_ASSOCIATION_MAPPER_DESTINATION_TYPES = [
  "salesforce",
  "salesforceSandbox",
];

export const MAPPING_ROW_COLUMNS = "minmax(0, 1fr) 32px minmax(0, 1fr)";
export const MAPPING_COLUMN_GAP = 2;
export const MAPPING_HANDLE_WIDTH = 12;
export const MAPPING_HANDLE_GAP = 2;
export const MAPPING_SOURCE_INSET = MAPPING_HANDLE_WIDTH + MAPPING_HANDLE_GAP;
// Space from a destination field's right edge to the row's right edge: the 32px settings and remove buttons and their 8px gaps.
export const MAPPING_BUTTONS_INSET = 20;

export const isMappingLockedByMatchBooster = (
  mapping: SyncConfigMapping | undefined,
  destinationOption: ExtendedOption | undefined,
): boolean =>
  typeof mapping?.from === "object" &&
  mapping.from.type === MappingType.BOOSTED &&
  destinationOption?.extendedType?.matchBoosterOptional !== true;

/**
 * Builds the Liquid template picker entries for the merged columns among the
 * given (flattened) column options.
 *
 * The relationship names come from the backend
 * (`SyncableColumn.merged_relationship_names`), which builds them in lockstep
 * with the merge path — including relationships on intermediate models that
 * are absent from the parent model's own relationships list. Merged columns
 * whose names or leaf column name are unavailable are omitted: dropping a
 * picker entry is strictly better than inserting a reference with missing
 * segments, which fails at sync time.
 */
export const getMergedColumnOptions = (
  columns: Array<Option | ColumnOption>,
): Array<{
  label: string;
  inlineDescription: string | undefined;
  value: string;
}> =>
  columns.flatMap((option) => {
    const value = option.value as ColumnReference | string;
    if (!isMergedColumn(value)) {
      return [];
    }

    const relationshipNames = option.mergedRelationshipNames;
    const columnName = value.column.name;
    if (!relationshipNames?.length || !columnName) {
      return [];
    }

    return [
      {
        label: option.label,
        inlineDescription:
          "modelName" in option && typeof option.modelName === "string"
            ? option.modelName
            : undefined,
        value: `merged['${[...relationshipNames, columnName].join(".")}']`,
      },
    ];
  });

export const extractEligibleInlineMapperColumns = (
  columns: ColumnOption[],
): { [column: string]: string[] | null } => {
  const allColumnProps = {};

  columns.forEach((column) => {
    if (column.metadata?.properties !== undefined) {
      allColumnProps[column.label] = column.metadata.properties;
    }
    if (column.options) {
      column.options.forEach((nestedColumn) => {
        if (nestedColumn.metadata?.properties !== undefined) {
          allColumnProps[nestedColumn.label] = nestedColumn.metadata.properties;
        }
      });
    }
  });

  return allColumnProps;
};

/**
 * Discovers the JSON shape of array/object columns directly from sample rows
 * of a live preview, keyed the same way `aliasRow` keys row data (alias or
 * name) so it lines up with `ColumnOption.label`.
 *
 * A column shows up here only if the sample happened to contain an array of
 * objects for it; an eligible column absent from the current preview simply
 * isn't in the returned map, rather than a strict guarantee of completeness.
 */
export const extractJsonColumnPropertiesFromRows = (
  rows: Array<Record<string, unknown>>,
): { [column: string]: string[] } => {
  const jsonColumns: { [column: string]: Set<string> } = {};

  rows.forEach((row) => {
    Object.keys(row).forEach((column) => {
      const value = row[column];
      if (!Array.isArray(value)) return;

      const firstEntry = value[0];
      if (
        typeof firstEntry !== "object" ||
        firstEntry === null ||
        Array.isArray(firstEntry)
      ) {
        return;
      }

      jsonColumns[column] ??= new Set();
      const properties = jsonColumns[column];
      Object.keys(firstEntry).forEach((prop) => properties.add(prop));
    });
  });

  return Object.fromEntries(
    Object.entries(jsonColumns).map(([column, props]) => [
      column,
      Array.from(props),
    ]),
  );
};

// A destination field is identified by (value, object), not value alone: the
// same field name can appear under multiple destination objects (e.g. the same
// "Occured At" field under both "Save to Favorite" and "Save Inspiration").
// These helpers build a composite identity so the picker can tell them apart.
// JSON.stringify sidesteps any delimiter collision and keeps "1" distinct from 1.
type DestinationFieldOption = {
  value: unknown;
  object?: { value?: unknown } | null;
};

export const destinationOptionKey = (option: DestinationFieldOption): string =>
  JSON.stringify([option.object?.value ?? null, option.value ?? null]);

export const mappingDestinationKey = (mapping: {
  to?: unknown;
  object?: unknown;
}): string => JSON.stringify([mapping.object ?? null, mapping.to ?? null]);

// Resolve the option a mapping points at, disambiguating by object. Falls back
// to a value-only match for legacy mappings that have a `to` but no `object`,
// preserving prior behavior for non-colliding fields and custom typed values.
export const findSelectedDestinationOption = <T extends DestinationFieldOption>(
  options: T[] | undefined,
  mapping: { to?: unknown; object?: unknown },
): T | undefined => {
  if (!options) {
    return undefined;
  }
  const key = mappingDestinationKey(mapping);
  return (
    options.find((option) => destinationOptionKey(option) === key) ??
    (mapping.object == null
      ? options.find((option) => option.value === mapping.to)
      : undefined)
  );
};

// Every case must carry `object` through: it is the other half of the
// mapping's destination identity (see destinationOptionKey above). Dropping it
// here makes two same-value destination fields indistinguishable again on the
// next render, so the picker "reverts" to the first value match (CP-1489).
export const enforceMappingType = (mapping: Record<string, any>): Mapping => {
  switch (mapping.type) {
    case "standard":
      return {
        type: MappingType.STANDARD,
        from: mapping.from,
        to: mapping.to,
        object: mapping.object,
        ignoreNull: mapping.ignoreNull,
        disablePIIHashing: mapping.disablePIIHashing,
        fieldType: mapping.fieldType,
        typeOptions: mapping.typeOptions,
      };
    case "reference":
      return {
        type: MappingType.REFERENCE,
        lookup: mapping.lookup,
        to: mapping.to,
        object: mapping.object,
        ignoreNull: mapping.ignoreNull,
      };
    case "static":
      return {
        type: MappingType.STATIC,
        to: mapping.to,
        object: mapping.object,
        value: mapping.value,
        valueType: mapping.valueType,
        fieldType: mapping.fieldType,
        typeOptions: mapping.typeOptions,
      };
    case "variable":
      return {
        type: MappingType.VARIABLE,
        to: mapping.to,
        object: mapping.object,
        variable: mapping.variable,
        fieldType: mapping.fieldType,
        typeOptions: mapping.typeOptions,
      };
    case "object":
      return {
        type: MappingType.OBJECT,
        from: mapping.from,
        to: mapping.to,
        object: mapping.object,
      };
    case "array":
      return {
        type: MappingType.ARRAY,
        from: mapping.from,
        to: mapping.to,
        object: mapping.object,
        children: mapping.children,
      };
    case "template":
      return {
        type: MappingType.TEMPLATE,
        to: mapping.to,
        object: mapping.object,
        template: mapping.template,
        fieldType: mapping.fieldType,
        typeOptions: mapping.typeOptions,
      };
    default:
      return mapping as Mapping;
  }
};

// Boosted mappings show the 1P warehouse column in parentheses. Visual audiences
// inherit those columns from the parent schema. The audience itself often has
// none. Aliases match sync-time isColumnForBoostedColumn: hashed email/phone
// still merge into Email/Phone, and AAID/IDFA merge into the 1P MAID column.
const FIRST_PARTY_SEMANTIC_ALIASES: Record<string, readonly string[]> = {
  email: ["email", "email_md5", "email_sha256"],
  phone: ["phone", "phone_md5", "phone_sha256"],
  AAID: ["MAID"],
  IDFA: ["MAID"],
};

export type BoostedFirstPartyColumn = {
  name: string;
  semantic_type?: string | null;
};

export type ModelWithBoostedFirstPartyColumns = {
  columns?: BoostedFirstPartyColumn[] | null;
  parent?: {
    columns?: BoostedFirstPartyColumn[] | null;
  } | null;
};

const columnMatchesBoostedSemanticType = (
  column: BoostedFirstPartyColumn,
  semanticType: string,
): boolean => {
  const aliases = FIRST_PARTY_SEMANTIC_ALIASES[semanticType];
  if (aliases) {
    return aliases.includes(column.semantic_type ?? "");
  }
  return column.semantic_type === semanticType;
};

export const findBoostedFirstPartyColumn = (
  model: ModelWithBoostedFirstPartyColumns | null | undefined,
  semanticType: string | undefined,
): BoostedFirstPartyColumn | undefined => {
  if (!semanticType) {
    return undefined;
  }

  const match = (columns: BoostedFirstPartyColumn[] | null | undefined) =>
    columns?.find((column) =>
      columnMatchesBoostedSemanticType(column, semanticType),
    );

  return match(model?.columns) ?? match(model?.parent?.columns);
};
